import React, { useState } from 'react';
import { Head, Section, ProjectWizard } from '@/components';
import { BRAND } from '@/data/constants';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';


import { submitContactForm } from '@/api/contact';
import type { ContactFormData } from '@/types';

/**
 * Contact page component.
 * Features the interactive ProjectWizard and company contact info.
 */
const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // error state is available for future use or to display toast notifications
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_error, setError] = useState<string | null>(null);



  const handleSubmit = async (data: ContactFormData) => {
    // Spam check
    if (data._gotcha) return;

    setIsSubmitting(true);
    setError(null);


    try {
      const result = await submitContactForm(data);
      if (result.success) {
        navigate('/thank-you');
      } else {

        setError(result.error || 'Something went wrong. Please try again.');
        alert(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again later.');
      console.error(err);
      alert('Failed to send message.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Head
        title="Contact Us | Kivosystems"
        description="Start a project with Kivosystems. Book a discovery call or request a quote."
        canonicalUrl="https://www.kivosystems.com/contact"
        url="https://www.kivosystems.com/contact"
        image="/logo.svg"
      />

      <Section className="bg-obsidian min-h-screen pt-32 pb-20 relative overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sapphire-900/40 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sapphire-900/20 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-display font-medium text-white mb-6">
            Work with <span className="text-sapphire-500">Kivosystems.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Ready to build something exceptional? Let's define your vision.
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-24 items-start">

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1 space-y-12 order-2 lg:order-1 pt-12 lg:pt-0 border-t lg:border-t-0 border-white/10">
            <div>
              <h3 className="text-lg font-bold mb-2 text-white">Business Inquiries</h3>
              <p className="text-gray-400 mb-4 leading-relaxed">
                For new project inquiries, please email us directly or use the interactive workflow on the right to start your journey.
              </p>
              <a href={`mailto:${BRAND.email}`} className="text-white border-b border-white pb-0.5 hover:text-sapphire-500 hover:border-sapphire-500 transition-colors">
                {BRAND.email}
              </a>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-2 text-white">Locations</h3>
              <div className="space-y-4 text-sm text-gray-400">
                <address className="not-italic">
                  <strong className="block text-white mb-1">United Kingdom</strong>
                  London
                </address>
                <address className="not-italic">
                  <strong className="block text-white mb-1">Cyprus</strong>
                  Island-wide
                </address>
              </div>
            </div>
          </div>

          {/* Wizard Container */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <AnimatePresence mode="wait">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 backdrop-blur-sm">
                <ProjectWizard onSubmit={handleSubmit} isSubmitting={isSubmitting} />
              </div>
            </AnimatePresence>

          </div>
        </div>
      </Section>
    </>
  );
};

export default Contact;
